// src/components/NotesManagerDialog.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NotesIcon from "@mui/icons-material/Notes";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { useNotification } from "../../interviewAI/NotificationContext"; // Adjust path as needed
import ConfirmationDialog from "./ConfirmationDialog"; // Import confirmation dialog
import { format } from "date-fns";
import { authFetch } from "../services/httpClient";

// delimiter used when storing multiple notes in single backend 'note' string
  const NOTE_DELIM = "\n\n---\n\n";

// Helper: saves notes array to backend (update-note API)
const saveNotesToServer = async (interviewId, notesArray) => {
  if (!interviewId) {
    console.error("Missing interview id in saveNotesToServer");
    throw new Error("Missing interview id");
  }
  try {
    const noteString = Array.isArray(notesArray)
      ? notesArray.map((n) => (n && n.text ? n.text.trim() : "")).filter(Boolean).join(NOTE_DELIM)
      : String(notesArray || "").trim();
    const res = await authFetch(`${process.env.REACT_APP_REST_BASE_URL}/interviews/update-note`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ interviewId, note: noteString }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || data.error || "Failed to update note");
    }
    // Optionally return the updated interview data if needed by parent
    return data;
  } catch (err) {
    console.error("Error saving notes to server:", err);
    throw err;
  }
};

const NotesManagerDialog = ({ open, onClose, interviewId, candidateName, refreshInterviews }) => {
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNoteIndex, setEditingNoteIndex] = useState(-1);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const { notify } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch notes when the dialog opens or the interviewId changes
  useEffect(() => {
    if (open && interviewId) {
      const fetchNotes = async () => {
        try {
          // Fetch the specific interview data to get its note string
          const res = await authFetch(`${process.env.REACT_APP_REST_BASE_URL}/interviews/${interviewId}`);
          if (!res.ok) {
             const errorData = await res.json();
             throw new Error(errorData.message || "Failed to fetch interview data");
          }
          const interviewData = await res.json();
          const raw = interviewData.note || "";
          const parsed = raw
            ? raw.split(NOTE_DELIM).map((t, idx) => ({
                id: `${Date.now()}_${idx}_${Math.floor(Math.random() * 1000)}`,
                text: String(t || "").trim(),
                timestamp: new Date().toISOString(), // Use current time for old notes
              }))
            : [];
          setNotes(parsed);
        } catch (err) {
          console.error("Error fetching notes for dialog:", err);
          notify("error", "Failed to load notes: " + err.message);
          setNotes([]); // Reset notes on error
        }
      };
      fetchNotes();
    } else {
      // Reset state when dialog closes or ID changes while closed
      setNotes([]);
      setNewNoteText("");
      setEditingNoteIndex(-1);
      setEditingNoteText("");
    }
  }, [open, interviewId, notify]);

  const handleAddNote = async () => {
    if (!interviewId) return notify("error", "No interview selected");
    if (newNoteText.trim() === "") return; // Don't add empty notes

    const newNote = {
      id: `${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      text: newNoteText.trim(),
      timestamp: new Date().toISOString(),
    };
    const updatedNotes = [...notes, newNote];
    setNotes(updatedNotes);
    setNewNoteText(""); // Clear the input field after adding

    try {
      await saveNotesToServer(interviewId, updatedNotes);
      // Optional: Refresh the main interview list to reflect the updated note count/icon
      if (refreshInterviews) {
          refreshInterviews();
      }
    } catch (err) {
      // Revert UI change on error - optional
      setNotes(prev => prev.filter(n => n.id !== newNote.id));
      console.error("Save notes failed:", err);
      notify("error", "Failed to save note: " + err.message);
    }
  };

  const handleStartEditNote = (index) => {
    setEditingNoteIndex(index);
    setEditingNoteText(notes[index].text);
  };

  const handleSaveEditNote = async () => {
    if (!interviewId) return notify("error", "No interview selected");
    if (editingNoteText.trim() === "") return; // Don't save empty edits

    const updatedNotes = [...notes];
    updatedNotes[editingNoteIndex] = {
      ...updatedNotes[editingNoteIndex],
      text: editingNoteText.trim(),
    };
    setNotes(updatedNotes);
    setEditingNoteIndex(-1); // Exit editing mode
    setEditingNoteText("");

    try {
      await saveNotesToServer(interviewId, updatedNotes);
      if (refreshInterviews) {
          refreshInterviews();
      }
    } catch (err) {
      console.error("Save edited note failed:", err);
      notify("error", "Failed to save edited note: " + err.message);
    }
  };

  const handleCancelEditNote = () => {
    setEditingNoteIndex(-1);
    setEditingNoteText("");
  };

  const handleDeleteNoteClick = (index) => {
    setPendingDeleteIndex(index);
    setConfirmationOpen(true);
  };

  const handleConfirmDeleteNote = async () => {
    if (pendingDeleteIndex === null) return;
    if (!interviewId) return notify("error", "No interview selected");

    const index = pendingDeleteIndex;
    const noteToDelete = notes[index];
    const updatedNotes = notes.filter((_, i) => i !== index);
    setNotes(updatedNotes);

    try {
      await saveNotesToServer(interviewId, updatedNotes);
      if (refreshInterviews) {
          refreshInterviews();
      }
    } catch (err) {
      // Revert UI change on error
      setNotes(prev => [...prev.slice(0, index), noteToDelete, ...prev.slice(index)]);
      console.error("Delete note failed:", err);
      notify("error", "Failed to delete note: " + err.message);
    } finally {
      setPendingDeleteIndex(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
        handleCancelEditNote();
    }
  };

  return (
<Dialog
  open={open}
  onClose={onClose}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 3,
      border: "2px dashed rgba(13, 115, 119, 0.2)",
      background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      minHeight: "auto",
      maxHeight: "85vh",
      display: "flex",
      flexDirection: "column",
      transition: "border-color 0.3s ease",
      "&:hover": {
        borderColor: "rgba(13, 115, 119, 0.4)",
      },
    },
  }}
  onKeyDown={handleKeyDown}
>
  <DialogTitle
    sx={{
      pb: 1,
      borderBottom: `1px solid rgba(13, 115, 119, 0.1)`,
      background: "rgba(13, 115, 119, 0.02)",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between", // Space between title and close button
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{ position: "relative", zIndex: 1 }}
    >
      <NotesIcon
        sx={{
          fontSize: 20,
          color: "#0d7377",
        }}
      />
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{ 
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
          color: "#0d7377",
        }}
      >
        Manage Notes for {candidateName || "Unknown Candidate"}
      </Typography>
    </Box>

    {/* Close Button - Top Right Corner */}
    <Box sx={{ position: "absolute", top: 8, right: 8 }}>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          color: "rgba(0, 0, 0, 0.6)",
          bgcolor: "rgba(13, 115, 119, 0.08)",
          "&:hover": {
            bgcolor: "rgba(13, 115, 119, 0.15)",
            color: "#0d7377",
          },
          transition: "all 0.2s ease",
          p: 0.7,
        }}
      >
        <ClearIcon fontSize="small" />
      </IconButton>
    </Box>
  </DialogTitle>
  
  <DialogContent 
    sx={{ 
      flex: 1, 
      p: 0,
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Add New Note Card */}
      <Box
        sx={{
          borderWidth: "2px",
          borderStyle: "dashed",
          borderColor: "rgba(13, 115, 119, 0.3)",
          background: "rgba(13, 115, 119, 0.02)",
          borderRadius: 2,
          transition: "border-color 0.3s ease",
          "&:hover": {
            borderColor: "rgba(13, 115, 119, 0.5)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <AddIcon
              sx={{
                fontSize: 16,
                color: "#0d7377",
              }}
            />
            <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.7)" }}>
              Add New Note
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              variant="outlined"
              placeholder="Type your note here..."
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleAddNote();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  background: "rgba(255, 255, 255, 0.8)",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.9)",
                  },
                  "&.Mui-focused": {
                    background: "rgba(255, 255, 255, 0.95)",
                  },
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                size="small"
                onClick={handleAddNote}
                disabled={!newNoteText.trim()}
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: "#0d7377",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#0a5a5d",
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}
              >
                Add Note
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Notes List */}
      {notes.length === 0 ? (
        <Box
          sx={{
            bgcolor: "rgba(0, 0, 0, 0.02)",
            border: "1px solid rgba(13, 115, 119, 0.1)",
            borderRadius: 2,
          }}
        >
          <Box sx={{ p: 4, textAlign: "center" }}>
            <NotesIcon
              sx={{
                fontSize: 48,
                color: "rgba(0, 0, 0, 0.3)",
                mx: "auto",
                mb: 2,
              }}
            />
            <Typography variant="body1" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
              No notes yet
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.4)", mt: 0.5 }}>
              Add your first note above
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {notes.map((note, index) => (
            <Box
              key={note.id || index}
              sx={{
                border: "1px solid rgba(13, 115, 119, 0.1)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                borderRadius: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(13, 115, 119, 0.1)",
                },
              }}
            >
              <Box sx={{ p: 2 }}>
                {editingNoteIndex === index ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={editingNoteText}
                      onChange={(e) => setEditingNoteText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          handleSaveEditNote();
                        }
                      }}
                      autoFocus
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          background: "rgba(255, 255, 255, 0.8)",
                        },
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCancelEditNote}
                        startIcon={<ClearIcon />}
                        sx={{
                          borderColor: "rgba(0, 0, 0, 0.2)",
                          color: "rgba(0, 0, 0, 0.6)",
                          "&:hover": {
                            borderColor: "rgba(0, 0, 0, 0.3)",
                            bgcolor: "rgba(0, 0, 0, 0.02)",
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        onClick={handleSaveEditNote}
                        disabled={!editingNoteText.trim()}
                        startIcon={<CheckIcon />}
                        sx={{
                          bgcolor: "#0d7377",
                          color: "white",
                          "&:hover": {
                            bgcolor: "#0a5a5d",
                          },
                          "&:disabled": {
                            opacity: 0.5,
                            cursor: "not-allowed",
                          },
                        }}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            lineHeight: 1.6,
                            fontSize: "1rem",
                            color: "rgba(0, 0, 0, 0.9)",
                          }}
                        >
                          {note.text}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleStartEditNote(index)}
                            sx={{
                              p: 0.5,
                              color: "rgba(13, 115, 119, 0.7)",
                              "&:hover": {
                                bgcolor: "rgba(13, 115, 119, 0.1)",
                                color: "#0d7377",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteNoteClick(index)}
                            sx={{
                              p: 0.5,
                              color: "rgba(255, 80, 80, 0.7)",
                              "&:hover": {
                                bgcolor: "rgba(255, 80, 80, 0.1)",
                                color: "#ff5050",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(0, 0, 0, 0.5)",
                          fontSize: "0.75rem",
                        }}
                      >
                        Created: {note.timestamp 
                          ? format(new Date(note.timestamp), "MMM dd, yyyy 'at' hh:mm a")
                          : 'No date'
                        }
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  </DialogContent>
  <ConfirmationDialog
    open={confirmationOpen}
    onClose={() => setConfirmationOpen(false)}
    onConfirm={handleConfirmDeleteNote}
    title="Delete Note"
    message="Are you sure you want to delete this note? This action cannot be undone."
    confirmText="Delete"
    cancelText="Cancel"
    isDangerous={true}
  />
</Dialog>
  );
};

export default NotesManagerDialog;
